<template>
  <div class="eligibilityState">
    <h2 class="question-title">
      Let’s make sure we’re available in your state.
    </h2>
    <b-row>
      <b-col>
        <div
          role="group"
          class="group-mb"
        >
          <label
            for="state"
            class="label-text"
          >{{ isOnboardingRedesign ? 'Which' : 'What' }} state do you live in?</label>
          <template v-if="isOnboardingRedesign">
            <CoreMobileSelect
              class="state-select mobile-select tw-block lg:tw-hidden tw-text-lg"
              :class="{'is-empty': isSelectionEmpty}"
              :model-value="state"
              :options="options"
              label-key="text"
              :placeholder="isOnboardingRedesign ? 'Select a state' : 'Select state'"
              @update:modelValue="$event => (state = $event)"
            >
              <template #open-indicator>
                {{ }}
                <svg
                  v-if="isOnboardingRedesign"
                  width="16"
                  height="9"
                  viewBox="0 0 16 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.00297 8.35386C7.79296 8.35386 7.60505 8.27373 7.43924 8.11345L1.02266 1.54765C0.950814 1.4758 0.895547 1.39566 0.856859 1.30723C0.818172 1.21328 0.798828 1.1138 0.798828 1.00879C0.798828 0.865092 0.831989 0.735213 0.89831 0.619151C0.964631 0.503089 1.05306 0.411897 1.16359 0.345576C1.27966 0.279254 1.40954 0.246094 1.55323 0.246094C1.76325 0.246094 1.94287 0.317942 2.09209 0.461638L8.44235 6.95283H7.55531L13.9056 0.461638C14.0603 0.317942 14.2399 0.246094 14.4444 0.246094C14.5881 0.246094 14.7152 0.279254 14.8258 0.345576C14.9418 0.411897 15.033 0.503089 15.0993 0.619151C15.1657 0.735213 15.1988 0.865092 15.1988 1.00879C15.1988 1.21328 15.1242 1.39014 14.975 1.53936L8.55841 8.11345C8.48657 8.19082 8.4009 8.25162 8.30142 8.29583C8.20746 8.33452 8.10798 8.35386 8.00297 8.35386Z"
                    fill="#19191C"
                  />
                </svg>
                <b-icon
                  v-else
                  v-bind="attributes"
                  icon="chevron-down"
                />
              </template>
            </CoreMobileSelect>
            <div class="tw-relative">
              <v-select
                v-model="state"
                class="v-select-state-select custom-input-style fs-mask tw-hidden lg:tw-block"
                :calculate-position="withPopper"
                label="text"
                :options="options"
                :searchable="true"
                :placeholder="isOnboardingRedesign ? 'Select a state' : 'Select state'"
                @open="openSelectMenu"
                @close="closeSelectMenu"
              >
                <template #open-indicator="{ attributes }">
                  <svg
                    v-if="isOnboardingRedesign"
                    width="16"
                    height="9"
                    viewBox="0 0 16 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.00297 8.35386C7.79296 8.35386 7.60505 8.27373 7.43924 8.11345L1.02266 1.54765C0.950814 1.4758 0.895547 1.39566 0.856859 1.30723C0.818172 1.21328 0.798828 1.1138 0.798828 1.00879C0.798828 0.865092 0.831989 0.735213 0.89831 0.619151C0.964631 0.503089 1.05306 0.411897 1.16359 0.345576C1.27966 0.279254 1.40954 0.246094 1.55323 0.246094C1.76325 0.246094 1.94287 0.317942 2.09209 0.461638L8.44235 6.95283H7.55531L13.9056 0.461638C14.0603 0.317942 14.2399 0.246094 14.4444 0.246094C14.5881 0.246094 14.7152 0.279254 14.8258 0.345576C14.9418 0.411897 15.033 0.503089 15.0993 0.619151C15.1657 0.735213 15.1988 0.865092 15.1988 1.00879C15.1988 1.21328 15.1242 1.39014 14.975 1.53936L8.55841 8.11345C8.48657 8.19082 8.4009 8.25162 8.30142 8.29583C8.20746 8.33452 8.10798 8.35386 8.00297 8.35386Z"
                      fill="#19191C"
                    />
                  </svg>
                  <b-icon
                    v-else
                    v-bind="attributes"
                    icon="chevron-down"
                  />
                </template>
                <span
                  v-if="states.length == 0"
                  slot="no-options"
                >Loading...</span>
              </v-select>
            </div>
          </template>
          <template v-else>
            <v-select
              v-model="state"
              class="custom-input-style fs-mask"
              append-to-body
              :calculate-position="withPopper"
              label="text"
              :options="states"
              :searchable="true"
              placeholder="Select state"
            >
              <template #open-indicator="{ attributes }">
                <b-icon
                  v-bind="attributes"
                  icon="chevron-down"
                />
              </template>
              <span
                v-if="states.length == 0"
                slot="no-options"
              >Loading...</span>
            </v-select>
          </template>
        </div>
        <!-- Terms and conditions -->

        <template v-if="isOnboardingRedesign">
          <b-form-checkbox
            id="terms"
            v-model="terms"
            class="checkbox-label onboarding-redesign"
            name="checkbox-1"
            value="accepted"
            unchecked-value="not_accepted"
          >
            <div class="tw-text-sm md:tw-text-base lg:tw-text-lg">
              <span>I have read and agree to the </span>
              <a
                href="#"
                @click.prevent="termsModal = true"
              >Terms &amp; Conditions</a>,
              <a
                href="#"
                @click.prevent="privacyModal = true"
              >Privacy Policy</a>, and
              <a
                href="#"
                @click.prevent="telehealthConsentModal = true"
              >Telehealth Consent</a>.
            </div>
          </b-form-checkbox>
        </template>
        <template v-else>
          <b-form-checkbox
            id="terms"
            v-model="terms"
            class="checkbox-label"
            name="checkbox-1"
            value="accepted"
            unchecked-value="not_accepted"
          >
            <span>I have read and agree to these </span>
            <a
              href="#"
              @click.prevent="termsModal = true"
            >
              Terms &amp; Conditions,
            </a>
            <a
              href="#"
              @click.prevent="termsModal = true"
            >Privacy Policy,</a> and
            <a
              href="#"
              @click.prevent="termsModal = true"
            >Telehealth Consent</a>
          </b-form-checkbox>
        </template>

        <LoginRouterLink />

        <OnboardingCTAContainer>
          <div class="tw-flex tw-flex-col tw-gap-2 tw-w-full tw-text-center">
            <div class="tw-h-full tw-w-full">
              <b-button
                class="slight-rounded"
                :class="{'tw-max-w-[300px] tw-mx-auto tw-text-base md:tw-text-lg': isOnboardingRedesign}"
                pill
                variant="info"
                block
                :disabled="isOnboardingRedesign ? !state : disabled"
                @click="submitState"
              >
                Continue
              </b-button>
            </div>
          </div>
        </OnboardingCTAContainer>
      </b-col>
    </b-row>

    <!-- Modals -->
    <TermsModal
      :show="termsModal"
      header-class="terms-title"
      body-class="terms-body"
      :full-width="isOnboardingRedesign ? true : false"
      @exit="termsModal = false"
    >
      <template #header>
        <h1
          v-if="!isOnboardingRedesign"
          class="h2 mb-0 heading text-primary pr-4"
        >
          Terms &amp; Conditions, Privacy Policy and Telehealth Consent
        </h1>
      </template>
      <template #body>
        <div class="tw-relative">
          <terms :class="isOnboardingRedesign ? 'tw-mt-0 tw-pb-[150px] md:tw-max-w-[706px] lg:tw-max-w-[1006px] tw-mx-auto' : 'tw-mt-10'" />
          <div :class="isOnboardingRedesign ? 'tw-text-center tw-fixed tw-bg-white tw-bottom-0 tw-left-0 tw-right-0 tw-px-4' : 'tw-text-left'">
            <b-button
              class="slight-rounded"
              :class="isOnboardingRedesign ? 'tw-my-4' : 'tw-mb-10'"
              pill
              variant="info"
              style="min-width: 250px"
              @click="agreeTerms()"
            >
              I Agree
            </b-button>
          </div>
        </div>
      </template>
    </TermsModal>
    <TermsModal
      :show="privacyModal"
      header-class="terms-title"
      body-class="terms-body"
      :full-width="isOnboardingRedesign ? true : false"
      @exit="privacyModal = false"
    >
      <template #header>
        <div></div>
      </template>
      <template #body>
        <div class="tw-relative">
          <privacy :class="{'md:tw-max-w-[706px] lg:tw-max-w-[1006px] tw-mx-auto': isOnboardingRedesign}" />
          <div :class="isOnboardingRedesign ? 'tw-text-center tw-fixed tw-bg-white tw-bottom-0 tw-left-0 tw-right-0 tw-px-4' : 'tw-text-left'">
            <b-button
              class="slight-rounded"
              :class="isOnboardingRedesign ? 'tw-my-4' : 'tw-mb-10'"
              pill
              variant="info"
              style="min-width: 250px"
              @click="agreeTerms()"
            >
              I Agree
            </b-button>
          </div>
        </div>
      </template>
    </TermsModal>
    <TermsModal
      :show="telehealthConsentModal"
      header-class="terms-title"
      body-class="terms-body"
      :full-width="isOnboardingRedesign ? true : false"
      @exit="telehealthConsentModal = false"
    >
      <template #header>
        <div></div>
      </template>
      <template #body>
        <div class="tw-relative">
          <TelehealthConsent :class="{'md:tw-max-w-[706px] lg:tw-max-w-[1006px] tw-mx-auto': isOnboardingRedesign}" />
          <div :class="isOnboardingRedesign ? 'tw-text-center tw-fixed tw-bg-white tw-bottom-0 tw-left-0 tw-right-0 tw-px-4' : 'tw-text-left'">
            <b-button
              class="slight-rounded"
              :class="isOnboardingRedesign ? 'tw-my-4' : 'tw-mb-10'"
              pill
              variant="info"
              style="min-width: 250px"
              @click="agreeTerms()"
            >
              I Agree
            </b-button>
          </div>
        </div>
      </template>
    </TermsModal>
    <div
      class="bg-overlay"
      @click="termsModal = false; privacyModal = false; telehealthConsentModal = false"
    ></div>

    <modal
      id="agreement"
      v-model="showModal"
      modal-class="agreement"
      size="sm"
      centered
      hide-header
      hide-footer
    >
      <h2 class="mb-0 text-center link">
        Winona’s treatments require our customers to agree to the terms
      </h2>
      <!-- <b-button @click="$bvModal.hide('agreement')">Hide Modal</b-button> -->
    </modal>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex';
import analyticsMixin from '@/mixins/analytics';
import popper from '@/assets/js/popper.js';
import formValidation from '@/components/form/mixins';

import { LookUpService } from '@/services/lookup.service';
import Terms from '@/components/partials/Terms';
import Privacy from '@/components/partials/Privacy';
import TelehealthConsent from '@/components/partials/TelehealthConsent';
import TermsModal from '@/components/partials/SideModal';
import LoginRouterLink from '@/components/partials/LoginRouterLink';
import OnboardingCTAContainer from '@/components/onboarding/OnboardingCTAContainer.vue';
import CoreMobileSelect from '@corefront/components-v2/CoreMobileSelect/CoreMobileSelect.vue';

export default {
  components: {
    Terms,
    Privacy,
    TelehealthConsent,
    TermsModal,
    LoginRouterLink,
    OnboardingCTAContainer,
    CoreMobileSelect
  },
  mixins: [analyticsMixin, formValidation, popper],
  props: {
    onboardingflow: {
      type: String,
      default: 'flowA',
    },
    preAnswer: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      state: null,
      states: [],
      stateData: [],
      showModal: false,
      termsModal: false,
      privacyModal: false,
      telehealthConsentModal: false,
      terms: '',
      disabled: false,
    };
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    ...mapState('onboarding', ['onboardingPatient']),
    options() {
      return [...this.states].sort((a, b) => a.text.localeCompare(b.text));
    },
    isSelectionEmpty() {
      return _.isEmpty(this.state) || this.state === ''
    }
  },
  async mounted() {
    this.$_analytics_track(
      'Onboarding Screen Viewed',
      {
        // customerId: <Customer ID.>,
        screen: '1.1-A',
        onboardingflow: this.onboardingflow,
      }
    );
    await this.getStates();
    if (this.preAnswer) {
      const selectedState = this.stateData.filter(
        state => state.id == this.preAnswer
      )[0];
      this.state = { value: selectedState.id, text: selectedState.name };
    }
  },
  methods: {
    openSelectMenu() {
      const el = document.querySelector('.onboarding-wrapper');
      if (el) el.style.height = '120vh';
    },
    closeSelectMenu() {
      const el = document.querySelector('.onboarding-wrapper');
      if (el) el.style.height = '';
    },
    async getStates() {
      const { data } = await LookUpService.getLookUp('state');
      // Raw state data
      this.stateData = data;
      // State data for options
      data.forEach(data => {
        this.states.push({ value: data.id, text: data.name });
      });
    },
    async submitState() {
      console.log(this.state.value)
      if (this.terms == '' || this.terms == 'not_accepted') {
        this.toggleModal();
      } else if (this.state.value == '') {
        this.$emit('showAlert', 'Please choose a state.');
      } else {
        const selectedState = this.stateData.filter(
          state => state.id == this.state.value
        )[0];
        if (!selectedState) {
          this.$emit('showAlert', 'Please choose a state.');
          return;
        }
        this.$emit('setParams', 'state', selectedState.id);
        this.$emit('setStateName', selectedState.name);
        // WIN-31756
        // Fire state selected regardless of support
        this.$_analytics_track(
          'State Selected',
          {
            state: this.state.value,
          },
        );

        if (selectedState?.supported) {
          // this.$emit('showLoader', 3)
          this.$emit('setStep', 'email');
        } else {
          const storedVariant = localStorage.getItem('flow_variant');
          this.$_analytics_track(
            'Onboarding Reject',
            {
              question: 'What state do you live in?',
              answer: this.state.value,
              onboardingflow: this.onboardingflow,
              ...(['H', 'J', '1225-onboarding-core-v1', 'K'].includes(storedVariant) && { rejectionType: 'Hard' }),
            },
          );

          this.$emit('setStep', 'waitlist');
        }
      }
    },
    toggleModal() {
      this.$root.$emit('bv::show::modal', 'agreement');
    },
    agreeTerms() {
      // this.$root.$emit("bv::hide::modal", "terms");
      this.termsModal = false;
      this.privacyModal = false;
      this.telehealthConsentModal = false;
      this.terms = 'accepted';
    },
  },
};
</script>

<style lang="scss" scoped>
.vs__dropdown-toggle {
  height: 52px;
}

html:not(.redesign) {
  .checkbox-label a {
    cursor: pointer;
    color: #a783ff;
    text-decoration: none;

    &:hover {
      color: #1a6a72 !important;
    }
  }
}

::v-deep .state-select button {
  color: #19191C !important;
}

::v-deep .state-select.is-empty button {
  color: #828282 !important;
}

::v-deep .state-select svg {
  fill: #19191C !important;
}

::v-deep .v-select-state-select .vs__dropdown-toggle {
  background-color: transparent !important;
  border: 1px solid #C7C7C7 !important;
  border-radius: 10px !important;
}

::v-deep .v-select-state-select .vs__open-indicator {
  fill: #19191C !important;
  transform: scale(1.25) !important;
}

::v-deep .state-select button {
  background-color: transparent !important;
  border: 1px solid #C7C7C7 !important;
}

::v-deep .mobile-select {
  &>div>div{
    z-index: 2147483003 !important;
  }
}
.redesign {
  .checkbox-label a {
    cursor: pointer;
    color: #1a6a72 !important;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}

::v-deep .checkbox-label.onboarding-redesign > .custom-control-label {
  &:before,
  &:after {
    top: 3px;

    @include media-breakpoint-down(lg) {
      top: 5px;
    }

    @include media-breakpoint-down(md) {
      top: 3px;
    }

  }
}

::v-deep .checkbox-label:not(.onboarding-redesign) > .custom-control-label {
  &:before,
  &:after {
    top: 2px;

    @include media-breakpoint-up(md) {
      top: 0px;
    }
  }
}

::v-deep .vs__dropdown-menu{
  top: 4rem !important;
  max-height: 20rem;
  position: absolute;
  overflow: auto;
  z-index: 9000000000000000 !important;
  .vs__dropdown-option:hover{
    background: #f3f3f3 !important;
    font-size: 18px !important;
  }

  .vs__dropdown-option--highlight {
    background: #f3f3f3 !important;
    font-size: 18px !important;
  }

}

</style>
